import { useState, useEffect } from "react";
import { Clock } from "lucide-react";
import { useActivePromo } from "@/hooks/useActivePromo";

export const CountdownTimer = () => {
  const { expiresAt, promo, isLoading } = useActivePromo();
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!expiresAt) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const endTime = expiresAt.getTime();
      const difference = endTime - now;

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [expiresAt]);

  const formatNumber = (num: number) => num.toString().padStart(2, "0");

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <Clock className="h-3.5 w-3.5 animate-pulse" />
        <span>Loading...</span>
      </div>
    );
  }

  if (isExpired || !expiresAt) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <Clock className="h-3.5 w-3.5" />
        <span>Offer expired</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-sm">
      <Clock className="h-3.5 w-3.5" />
      <span>Ends in</span>
      <div className="flex items-center gap-1 font-mono font-bold tracking-wider">
        {timeLeft.days > 0 && (
          <>
            <span className="bg-background/20 px-1.5 py-0.5 rounded">
              {timeLeft.days}d
            </span>
            <span className="mx-0.5">:</span>
          </>
        )}
        <span className="bg-background/20 px-1.5 py-0.5 rounded">
          {formatNumber(timeLeft.hours)}
        </span>
        <span>:</span>
        <span className="bg-background/20 px-1.5 py-0.5 rounded">
          {formatNumber(timeLeft.minutes)}
        </span>
        <span>:</span>
        <span className="bg-background/20 px-1.5 py-0.5 rounded">
          {formatNumber(timeLeft.seconds)}
        </span>
      </div>
    </div>
  );
};
